import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { collection, getDocs, query, where, updateDoc, addDoc, doc, getDoc, increment, setDoc, arrayRemove, arrayUnion, serverTimestamp, deleteDoc, orderBy, collectionGroup, runTransaction, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, Cog6ToothIcon, CheckIcon, XMarkIcon, PencilIcon, GlobeAltIcon } from '@heroicons/react/24/solid';
import MemberSearchWidget from '../components/MemberSearchWidget';

import ImageUpload from '../components/ImageUpload';
import { useAuth } from '../contexts/authContext';
import { Tab } from '@headlessui/react';
import { classNames } from '../utils/classNames';
import OrgMessageWriter from '../components/OrgMessageWriter';
import OrganizationAnnouncementsList from '../components/OrganizationAnnouncementsList';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import Drawer from '../components/Drawer';
import OrganizationSubscription from './organizations/OrganizationSubscription';
import OrganizationActivityFeed from './organizations/OrganizationActivityFeed';
import OrganizationGreatCommissionStats from './organizations/OrganizationGreatCommissionStats';
import OrganizationWelcomeSettings from './organizations/OrganizationWelcomeSettings';
import InviteMembers from './organizations/InviteMembers';
import { notifyAdminsOfProvisionalLicense } from '../utils/licenseNotifications';
// import { getMessaging, getToken, isSupported, connectMessagingEmulator } from "firebase/messaging";

// Comment out the emulator constants
// const isDevelopment = window.location.hostname === 'localhost';
// const EMULATOR_HOST = 'http://localhost:9198'; // Messaging emulator port

// Add this helper function at the top of the file, after imports
const formatWebsiteUrl = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
};

function Organization() {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isInvitePath = location.pathname.endsWith('/invite');
  const { currentUser } = useAuth();
  const [organization, setOrganization] = useState({
    name: '',
    displayName: '',
    bio: '',
    vitals: {
      country: '',
      zipCode: '',
      website: ''
    },
    settings: {},
    users: [],
    archived: false,
    logoUrl: ''
  });
  const [removeConfirmation, setRemoveConfirmation] = useState({});
  const [memberData, setMemberData] = useState([]);
  const [archivedMemberData, setArchivedMemberData] = useState([]);

  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [editedSettings, setEditedSettings] = useState({
    visibility: 'public',     // 'public' or 'private'
    requireAdminApproval: true, // boolean
    allowMembersToInvite: false, // boolean
    defaultRole: 'Member',
    primaryColor: '#4a90e2'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [logoUrl, setLogoUrl] = useState(organization?.logoUrl || '');
  const [isEditing, setIsEditing] = useState({
    bio: false,
    website: false
  });
  const [editValues, setEditValues] = useState({
    bio: '',
    website: ''
  });
  const [pendingRequests, setPendingRequests] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [orgMetrics, setOrgMetrics] = useState({
    activeMembers: 0,
    totalMembers: 0,
    pendingRequests: 0,
    totalGroups: 0,
    memberTrends: {
      lastWeek: 0,
      lastMonth: 0,
      lastQuarter: 0
    },
    activityStats: {
      lastActive: null,
      activeUsersThisWeek: 0,
      activeUsersThisMonth: 0
    }
  });
  const [orgStories, setOrgStories] = useState([]);
  const [orgContentTracks, setOrgContentTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [adminTab, setAdminTab] = useState('community'); // community | announcements | stats | members | account | welcome | content-tracks
  const [communitySubTab, setCommunitySubTab] = useState('stories'); // 'stories' | 'activity'
  const [userRole, setUserRole] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  // const [notificationPermission, setNotificationPermission] = useState('default');

  // Add new state for vitals display/editing
  const [isEditingVitals, setIsEditingVitals] = useState(false);
  const [editedVitals, setEditedVitals] = useState({
    country: '',
    zipCode: '',
    website: ''
  });
  const [counselingUrl, setCounselingUrl] = useState('');

  // Add new state for "Show More" metrics
  const [showMoreMetrics, setShowMoreMetrics] = useState(false);

  // Add new state for story filter
  const [storyFilter, setStoryFilter] = useState('visible');

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [discipleCount, setDiscipleCount] = useState(0);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);
  const [announcementsRefreshKey, setAnnouncementsRefreshKey] = useState(0);

  useEffect(() => {
    if (organization?.logoUrl) {
      setLogoUrl(organization.logoUrl);
    }
  }, [organization?.logoUrl]);

  // Sync admin tab with URL (e.g. after redirect from /organization/:orgId/subscription)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (['community', 'announcements', 'stats', 'members', 'account', 'welcome', 'content-tracks'].includes(tab)) {
      setAdminTab(tab);
    }
  }, [searchParams]);

  // When on /invite path, show Members tab as active
  useEffect(() => {
    if (isInvitePath) setAdminTab('members');
  }, [isInvitePath]);

  console.log("isMessageDrawerOpen state:", isMessageDrawerOpen);

  // New function to handle resending an invitation
  const handleResend = async (invite) => {
    const toastId = toast.loading('Resending invitation...');
    try {
      // Find the organization admin's name to use in the email
      const adminUser = memberData.find(m => m.uid === invite.invitedBy);
      const inviterName = adminUser ? adminUser.name : (organization.name || organization.displayName);

      const functions = getFunctions();
      // connectFunctionsEmulator(functions, "127.0.0.1", 5001); // Uncomment for local testing
      
      const sendInvitation = httpsCallable(functions, 'sendInvitationEmail');
      const result = await sendInvitation({
        recipientEmail: invite.email,
        organizationId: orgId,
        organizationName: organization.name || organization.displayName,
        inviteId: invite.id
      });

      console.log('Invitation function result:', result);

      if (result.data.success) {
        // Update the timestamp on the invitation to show it was resent
        const inviteRef = doc(db, 'organizations', orgId, 'invitations', invite.id);
        await updateDoc(inviteRef, { resentAt: serverTimestamp() });
        
        // Manually update the state to reflect the change immediately
        setPendingInvites(prevInvites => 
          prevInvites.map(i => 
            i.id === invite.id ? { ...i, resentAt: new Date() } : i
          )
        );

        toast.success(`Invitation resent to ${invite.email}`, { id: toastId });
      } else {
        throw new Error(result.data.error || 'The function reported a failure.');
      }
    } catch (error) {
      console.error("Error resending invitation:", error);
      toast.error(`Failed to resend invitation: ${error.message}`, { id: toastId });
    }
  };

  // New function to handle revoking an invitation
  const handleRevoke = async (inviteId) => {
    try {
      const inviteRef = doc(db, 'organizations', orgId, 'invitations', inviteId);
      await deleteDoc(inviteRef);
      // Update the state to remove the revoked invitation from the UI
      setPendingInvites(prev => prev.filter(invite => invite.id !== inviteId));
      toast.success("Invitation has been revoked.");
    } catch (error) {
      console.error("Error revoking invitation:", error);
      toast.error("Failed to revoke invitation. Please try again.");
    }
  };

  useEffect(() => {
    async function fetchOrgData() {
      try {
        const orgRef = doc(db, 'organizations', orgId);
        const orgSnap = await getDoc(orgRef);
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          // Normalize fields that may live in multiple locations
          const canonicalName = orgData.name || orgData.displayName;
          const canonicalBio = orgData.settings?.bio || orgData.tagline || orgData.bio;
          orgData.name = canonicalName;
          orgData.displayName = canonicalName;
          orgData.bio = canonicalBio;
          setOrganization(orgData);
          setLogoUrl(orgData.logoUrl || '');
          setCounselingUrl(orgData.counselingUrl || '');

          // Auto-heal: sync stale fields back to Firestore
          const healUpdates = {};
          if (orgData.displayName !== canonicalName || orgData.name !== canonicalName) {
            healUpdates.displayName = canonicalName;
            healUpdates.name = canonicalName;
          }
          if ((orgSnap.data().bio || '') !== canonicalBio) {
            healUpdates.bio = canonicalBio;
            healUpdates.tagline = canonicalBio;
          }
          if ((orgSnap.data().displayName || '') !== canonicalName) {
            healUpdates.displayName = canonicalName;
          }
          if ((orgSnap.data().tagline || '') !== canonicalBio) {
            healUpdates.tagline = canonicalBio;
          }
          if (Object.keys(healUpdates).length > 0) {
            updateDoc(orgRef, healUpdates).catch(err =>
              console.warn('Auto-heal org fields failed:', err)
            );
          }
        } else {
          console.error('No such organization!');
        }
      } catch (error) {
        console.error('Failed to fetch organization:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchOrgData();
  }, [orgId]);

  // Redirect users who have left the org so they no longer have access to the community
  useEffect(() => {
    if (!currentUser?.uid || !orgId) return;

    const checkAccess = async () => {
      try {
        const inActiveOrgs = (await getDoc(doc(db, 'users', currentUser.uid))).data()?.activeOrgs?.includes(orgId);
        if (!inActiveOrgs) {
          navigate('/my-community', { replace: true });
          return;
        }
        const memberRef = doc(db, 'organizations', orgId, 'members', currentUser.uid);
        const memberSnap = await getDoc(memberRef);
        const status = memberSnap.exists() ? memberSnap.data().status : null;
        if (status === 'inactive' || status === 'archived') {
          navigate('/my-community', { replace: true });
        }
      } catch (e) {
        console.error('Error checking org access:', e);
      }
    };

    checkAccess();
  }, [currentUser?.uid, orgId, navigate]);

  useEffect(() => {
    if (!orgId) return;

    const fetchMemberAndDiscipleCounts = async () => {
      try {
        const membersQuery = query(collection(db, 'users'), where('activeOrgs', 'array-contains', orgId));
        const membersSnapshot = await getDocs(membersQuery);
        const memberList = membersSnapshot.docs.map(d => d.data());
        
        const totalMembers = memberList.length;
        const totalDisciples = memberList.reduce((sum, member) => sum + (member.discipleIds?.length || 0), 0);

        setMemberCount(totalMembers);
        setDiscipleCount(totalDisciples);

      } catch (error) {
        console.error("Error fetching member and disciple counts:", error);
      }
    };

    fetchMemberAndDiscipleCounts();
  }, [orgId]);

  useEffect(() => {
    async function fetchMemberData() {
      if (!organization.users || organization.users.length === 0) return;
      
      try {
        const memberPromises = organization.users.map(async (userObj) => {
          const userRef = doc(db, 'users', userObj.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            return {
              ...userSnap.data(),
              role: userObj.role,
              uid: userObj.uid,
              authId: userSnap.id
            };
          }
          return null;
        });

        const members = await Promise.all(memberPromises);
        console.log('Fetched member data:', members);
        setMemberData(members.filter(member => member !== null));
      } catch (error) {
        console.error('Failed to fetch member data:', error);
      }
    }

    fetchMemberData();
  }, [organization.users]);

  useEffect(() => {
    if (organization.settings) {
      const settings = { ...organization.settings };
      // Migrate legacy joinProcess field to requireAdminApproval
      if (settings.joinProcess !== undefined && settings.requireAdminApproval === undefined) {
        settings.requireAdminApproval = settings.joinProcess === 'manual';
      }
      if (settings.requireAdminApproval === undefined) {
        settings.requireAdminApproval = true;
      }
      if (settings.allowMembersToInvite === undefined) {
        settings.allowMembersToInvite = false;
      }
      setEditedSettings(settings);
    }
  }, [organization.settings]);

  const fetchPendingRequests = useCallback(async () => {
    if (!orgId) return;
    try {
      const requestsQuery = query(
        collection(db, 'organizations', orgId, 'membershipRequests'),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(requestsQuery);
      const requests = snapshot.docs.map(d => ({
        userId: d.data().userId || d.id,
        ...d.data()
      }));
      setPendingRequests(requests);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  }, [orgId]);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!currentUser?.uid || !orgId) return;
      
      try {
        const orgUserRef = doc(db, 'organizations', orgId, 'users', currentUser.uid);
        const orgUserSnap = await getDoc(orgUserRef);
        
        if (orgUserSnap.exists()) {
          setUserRole(orgUserSnap.data().role);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [currentUser?.uid, orgId]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser?.uid || !orgId) {
        setIsAdmin(false);
        return;
      }

      try {
        // Source of truth: organization's members subcollection
        const orgMemberRef = doc(db, 'organizations', orgId, 'members', currentUser.uid);
        const orgMemberSnap = await getDoc(orgMemberRef);
        if (orgMemberSnap.exists()) {
          const data = orgMemberSnap.data();
          if (data.status === 'inactive') { setIsAdmin(false); return; }
          const role = data.role;
          const isAdminRole =
            role?.toLowerCase() === 'admin' ||
            role?.toLowerCase() === 'creator' ||
            role === 'Admin' ||
            role === 'Creator' ||
            data.isCreator === true;
          if (isAdminRole) {
            setIsAdmin(true);
            return;
          }
        }
        // Fallback: organization's users subcollection (legacy)
        const orgUserRef = doc(db, 'organizations', orgId, 'users', currentUser.uid);
        const orgUserSnap = await getDoc(orgUserRef);
        if (orgUserSnap.exists()) {
          const role = orgUserSnap.data().role;
          const isAdminRole =
            role?.toLowerCase() === 'admin' ||
            role?.toLowerCase() === 'creator' ||
            role === 'Admin' ||
            role === 'Creator' ||
            orgUserSnap.data().isCreator === true;
          if (isAdminRole) {
            setIsAdmin(true);
            return;
          }
        }
        // Fallback: user's own organizations subcollection
        const userOrgRoleRef = doc(db, 'users', currentUser.uid, 'organizations', orgId);
        const userOrgRoleSnap = await getDoc(userOrgRoleRef);
        
        if (userOrgRoleSnap.exists()) {
          const role = userOrgRoleSnap.data().role;
          setIsAdmin(
            role?.toLowerCase() === 'admin' || 
            role?.toLowerCase() === 'creator' ||
            role === 'Admin' ||
            role === 'Creator'
          );
        } else {
          // Check if user is the organization creator
          if (organization?.createdBy) {
            const creatorId = typeof organization.createdBy === 'string' 
              ? organization.createdBy 
              : organization.createdBy?.uid || organization.createdBy?.id;
            setIsAdmin(creatorId === currentUser.uid);
          } else {
            setIsAdmin(false);
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, orgId, organization]);

  const handleSettingsSave = async () => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      // Extract bio from editedSettings since it's a top-level org field, not a setting
      const { bio: editedBio, ...settingsOnly } = editedSettings;
      const settingsToSave = {
        ...settingsOnly,
        joinProcess: editedSettings.requireAdminApproval ? 'manual' : 'auto',
        requireApproval: editedSettings.requireAdminApproval,
        allowAutoJoin: !editedSettings.requireAdminApproval,
      };
      const updatePayload = {
        settings: settingsToSave,
      };
      // Save bio as top-level field (and tagline for mobile compatibility)
      if (editedBio !== undefined) {
        updatePayload.bio = editedBio;
        updatePayload.tagline = editedBio;
      }
      // Save counseling URL as top-level field
      updatePayload.counselingUrl = counselingUrl.trim() || null;

      await updateDoc(orgRef, updatePayload);
      
      setOrganization(prev => ({
        ...prev,
        settings: settingsToSave,
        ...(editedBio !== undefined ? { bio: editedBio, tagline: editedBio } : {}),
        counselingUrl: counselingUrl.trim() || null,
      }));
      
      setIsEditingSettings(false);
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const handleArchiveOrganization = async (orgId) => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      await updateDoc(orgRef, {
        archived: true
      });
      
      // Optionally redirect or show a message
      window.location.href = '/dashboard'; // If you want to redirect after archiving
    } catch (error) {
      console.error('Failed to archive organization:', error);
    }
  };

  const handleRoleChange = async (userId, currentRole, newRole) => {
    try {
      // Update in organization's users subcollection
      const orgUserRef = doc(db, 'organizations', orgId, 'users', userId);
      
      // Update in user's organizations subcollection
      const userOrgRef = doc(db, 'users', userId, 'organizations', orgId);

      if (newRole === 'Remove') {
        // Remove user from both collections
        await deleteDoc(orgUserRef);
        await deleteDoc(userOrgRef);
      } else {
        // Update role in both places
        const updateData = {
          role: newRole,
          lastActivityAt: serverTimestamp()
        };
        
        await updateDoc(orgUserRef, updateData);
        await updateDoc(userOrgRef, updateData);
      }

      // Refresh member data
      fetchMembers();
    } catch (error) {
      console.error('Error updating member role:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const usersRef = collection(db, 'organizations', orgId, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const membersData = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          const userData = await getDoc(doc.ref.parent.parent.parent.doc(doc.id));
          return {
            id: doc.id,
            ...userData.data(),
            membership: doc.data() // Include membership details
          };
        })
      );
      
      setMemberData(membersData);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleLogoUpload = async (downloadURL) => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      await updateDoc(orgRef, {
        logoUrl: downloadURL
      });
      setLogoUrl(downloadURL);
    } catch (error) {
      console.error('Failed to update organization logo:', error);
    }
  };

  const handleEditSave = async (field) => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      const updatePayload = { [field]: editValues[field] };
      // Keep mobile (tagline) and web (bio) fields in sync
      if (field === 'bio') updatePayload.tagline = editValues[field];
      if (field === 'tagline') updatePayload.bio = editValues[field];
      await updateDoc(orgRef, updatePayload);
      
      setOrganization(prev => ({
        ...prev,
        ...updatePayload,
      }));
      
      setIsEditing(prev => ({
        ...prev,
        [field]: false
      }));
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
    }
  };

  const fetchMembersAndInvites = async () => {
    try {
      // Single source of truth: organizations/{orgId}/members. Only active members.
      const [membersSnap, orgSnap] = await Promise.all([
        getDocs(collection(db, 'organizations', orgId, 'members')),
        getDoc(doc(db, 'organizations', orgId)),
      ]);
      const orgData = orgSnap.exists() ? orgSnap.data() : {};
      const creatorId = orgData.createdBy
        ? (typeof orgData.createdBy === 'string' ? orgData.createdBy : orgData.createdBy?.uid || orgData.createdBy?.id)
        : orgData.creatorId;

      const membersData = [];
      const seenIds = new Set();
      for (const memberDoc of membersSnap.docs) {
        const data = memberDoc.data();
        if (data.status === 'inactive' || data.status === 'denied' || data.status === 'archived') continue;
        const userId = memberDoc.id;
        seenIds.add(userId);
        let name = data.name;
        let email = data.email;
        let photo = data.photoURL || data.photo;
        if (!name || !email) {
          try {
            const userSnap = await getDoc(doc(db, 'users', userId));
            if (userSnap.exists()) {
              const u = userSnap.data();
              name = name || u.name || '';
              email = email || u.email || '';
              photo = photo || u.photoURL || u.photo;
            }
          } catch (e) {
            console.warn('Could not load user profile for member', userId, e);
          }
        }
        const isCreator = data.isCreator || userId === creatorId;
        membersData.push({
          id: userId,
          appID: data.appID ?? userId,
          ...data,
          name: name || email || 'Member',
          email: email || '',
          photo,
          requestedAt: data.requestedAt || data.joinedAt,
          role: isCreator ? 'Creator' : (data.role || 'Member'),
          isCreator: !!isCreator,
        });
      }

      // Ensure founding creator is listed (they may not have a members doc for legacy orgs)
      if (creatorId && !seenIds.has(creatorId)) {
        let name = '';
        let email = '';
        let photo = '';
        try {
          const userSnap = await getDoc(doc(db, 'users', creatorId));
          if (userSnap.exists()) {
            const u = userSnap.data();
            name = u.name || '';
            email = u.email || '';
            photo = u.photoURL || u.photo || '';
          }
        } catch (e) {
          console.warn('Could not load creator profile:', e);
        }
        const creatorInfo = typeof orgData.createdBy === 'object' ? orgData.createdBy : {};
        membersData.unshift({
          id: creatorId,
          appID: creatorInfo.appID || creatorId,
          name: name || creatorInfo.name || email || 'Creator',
          email: email || creatorInfo.email || '',
          photo,
          role: 'Creator',
          isCreator: true,
          status: 'active',
          requestedAt: orgData.createdAt,
        });
      }

      // Fetch pending invites from the correct subcollection
      const invitesRef = collection(db, 'organizations', orgId, 'invitations');
      const invitesQuery = query(
        invitesRef,
        where('status', '==', 'pending')
      );
      const invitesSnapshot = await getDocs(invitesQuery);
      const invitesData = invitesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        requestedAt: doc.data().createdAt || serverTimestamp()
      }));

      // Fetch archived members
      const archivedData = [];
      for (const memberDoc of membersSnap.docs) {
        const data = memberDoc.data();
        if (data.status !== 'archived') continue;
        const userId = memberDoc.id;
        let name = data.name;
        let email = data.email;
        let photo = data.photoURL || data.photo;
        if (!name || !email) {
          try {
            const userSnap = await getDoc(doc(db, 'users', userId));
            if (userSnap.exists()) {
              const u = userSnap.data();
              name = name || u.name || '';
              email = email || u.email || '';
              photo = photo || u.photoURL || u.photo;
            }
          } catch (e) {
            console.warn('Could not load user profile for archived member', userId, e);
          }
        }
        archivedData.push({
          id: userId,
          appID: data.appID ?? userId,
          ...data,
          name: name || email || 'Member',
          email: email || '',
          photo,
        });
      }

      setMemberData(membersData);
      setArchivedMemberData(archivedData);
      setPendingInvites(invitesData);
      setMemberCount(membersData.length);
    } catch (error) {
      console.error('Error fetching members and invites:', error);
    }
  };

  const handleRequestAction = async (userId, action, inviteId = null) => {
    try {
      if (inviteId) {
        // Handle INVITATION revoke (admin cancels a pending invitation)
        const inviteRef = doc(db, 'organizations', orgId, 'invitations', inviteId);
        const inviteSnap = await getDoc(inviteRef);

        if (inviteSnap.exists()) {
          if (action === 'deny') {
            await updateDoc(inviteRef, {
              status: 'revoked',
              revokedAt: serverTimestamp(),
              revokedBy: currentUser.uid
            });
            // Free the held license seat if there was a reservation
            const reservationRef = doc(db, 'organizations', orgId, 'licenseReservations', inviteId);
            const reservationSnap = await getDoc(reservationRef);
            if (reservationSnap.exists()) {
              await deleteDoc(reservationRef);
            }
          }
        }
      } else {
        // This logic handles MEMBERSHIP REQUESTS from the Join Drawer
        const requestRef = doc(db, 'organizations', orgId, 'membershipRequests', userId);
        const userRef = doc(db, 'users', userId);
        
        if (action === 'approve') {
          // Update request status to approved
          await updateDoc(requestRef, {
            status: 'approved',
            approvedAt: serverTimestamp(),
            approvedBy: currentUser.uid
          });

          // Add user to the organization's members and users (both used for member list)
          const request = pendingRequests.find(r => r.userId === userId);
          const memberData = {
            role: organization.settings?.defaultRole || 'Member',
            status: 'active',
            joinedAt: serverTimestamp()
          };
          await setDoc(doc(db, 'organizations', orgId, 'members', userId), memberData);
          await setDoc(doc(db, 'organizations', orgId, 'users', userId), {
            ...memberData,
            appID: userId,
            name: request?.name,
            email: request?.email
          }, { merge: true });

          // Add the org to the user's `activeOrgs` array
          await updateDoc(userRef, {
            activeOrgs: arrayUnion(orgId)
          });
          
          // Update the user's subcollection for this org to be active
          await updateDoc(doc(db, 'users', userId, 'organizations', orgId), {
            status: 'active',
            role: organization.settings?.defaultRole || 'Member'
          });

          // Always allocate a license on approval (full or provisional)
          try {
            const orgRef = doc(db, 'organizations', orgId);
            const memberRef = doc(db, 'organizations', orgId, 'members', userId);
            const allocationRef = doc(db, 'organizations', orgId, 'licenseAllocations', userId);
            const allocationHistoryRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');

            // Provisional count must be read outside transaction (Firestore web SDK: transaction.get only accepts DocumentReference)
            let provisionalCount = 0;
            try {
              const provisionalSnap = await getDocs(
                query(
                  collection(db, 'organizations', orgId, 'licenseAllocations'),
                  where('status', '==', 'provisional')
                )
              );
              provisionalCount = provisionalSnap.size;
            } catch (e) {
              console.warn('Could not count provisional licenses:', e);
            }

            const licenseResult = await runTransaction(db, async (transaction) => {
              const orgSnap = await transaction.get(orgRef);
              const allocationSnap = await transaction.get(allocationRef);

              if (!orgSnap.exists()) return null;
              if (allocationSnap.exists()) return null;

              const orgData = orgSnap.data();
              const currentLicense = orgData?.license || {};
              let currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
              const currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;

              // Default to 5 seats (first 5 included) when org has active subscription but no disciplemakers set
              const hasActiveLicense = currentLicense.status === 'active' ||
                currentLicense.status === 'trialing' ||
                currentLicense.platformSubscriptionId ||
                currentLicense.stripeSubscriptionId ||
                currentLicense.earlyAdopter === true;
              if (currentTotal === 0 && hasActiveLicense) {
                currentTotal = 5;
              }

              if (currentTotal === 0) return null;

              const seatsAvailable = currentTotal - currentUsed;
              let licenseStatus = 'allocated';
              let expiresAt = null;

              if (seatsAvailable <= 0) {
                const maxProvisional = currentLicense.maxProvisional ?? 3;
                if (provisionalCount >= maxProvisional) return null;
                licenseStatus = 'provisional';
                expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
              }

              transaction.set(allocationRef, {
                userId,
                status: licenseStatus,
                allocatedAt: serverTimestamp(),
                allocatedBy: currentUser.uid,
                reason: 'join_request_approved',
                organizationId: orgId,
                expiresAt,
                revokedAt: null,
                revokedBy: null,
              });

              const historyDocRef = doc(allocationHistoryRef);
              transaction.set(historyDocRef, {
                userId,
                action: 'allocated',
                status: licenseStatus,
                allocatedBy: currentUser.uid,
                reason: 'join_request_approved',
                organizationId: orgId,
                timestamp: serverTimestamp(),
              });

              transaction.update(memberRef, {
                licenseAllocated: true,
                licenseStatus,
                licenseAllocatedAt: serverTimestamp(),
                licenseExpiresAt: expiresAt,
              });

              if (licenseStatus === 'allocated') {
                const newUsed = currentUsed + 1;
                const newAvailable = currentTotal - newUsed;
                const updateData = {
                  'license.disciplemakersUsed': newUsed,
                  'license.disciplemakersAvailable': newAvailable,
                  'license.seatsUsed': newUsed,
                  'license.seatsAvailable': newAvailable,
                  'license.updatedAt': serverTimestamp(),
                };
                // Persist effective total when using first-5 fallback so org document stays consistent
                if ((currentLicense.disciplemakers ?? currentLicense.seats ?? 0) === 0) {
                  updateData['license.disciplemakers'] = 5;
                  updateData['license.seats'] = 5;
                }
                transaction.update(orgRef, updateData);
              }

              return { licenseStatus };
            });

            // Notify admins if a provisional license was issued
            if (licenseResult?.licenseStatus === 'provisional') {
              const request = pendingRequests.find(r => r.userId === userId);
              notifyAdminsOfProvisionalLicense({
                orgId,
                orgName: organization?.name || organization?.displayName,
                userName: request?.name || 'A new member',
                userId,
              });
            }
          } catch (licenseError) {
            console.warn('Could not allocate license on approval:', licenseError);
          }

        } else {
          // Deny request
          await updateDoc(requestRef, {
            status: 'denied',
            deniedAt: serverTimestamp(),
            deniedBy: currentUser.uid
          });
          // Also update the user's sub-collection doc
          await updateDoc(doc(db, 'users', userId, 'organizations', orgId), {
            status: 'denied'
          });
        }
      }

      // Refresh the members, invites, and pending requests lists
      fetchMembersAndInvites();
      fetchPendingRequests();
    } catch (error) {
      console.error('Error handling request/invite:', error);
    }
  };

  const handleArchiveMember = async (userId) => {
    console.log('[Archive] Starting archive for userId:', userId);
    try {
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      const orgUserRef = doc(db, 'organizations', orgId, 'users', userId);
      const userRef = doc(db, 'users', userId);
      const userOrgRef = doc(db, 'users', userId, 'organizations', orgId);
      const allocationRef = doc(db, 'organizations', orgId, 'licenseAllocations', userId);
      const orgRef = doc(db, 'organizations', orgId);
      const allocationHistoryRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');

      await runTransaction(db, async (transaction) => {
        // --- ALL READS FIRST (Firestore requirement) ---
        const orgSnap = await transaction.get(orgRef);
        const memberSnap = await transaction.get(memberRef);
        const allocationSnap = await transaction.get(allocationRef);
        const orgUserSnap = await transaction.get(orgUserRef);
        const userOrgSnap = await transaction.get(userOrgRef);

        if (!orgSnap.exists() || !memberSnap.exists()) throw new Error('Member or organization not found');

        const memberData = memberSnap.data();
        if (memberData.status === 'archived') throw new Error('Member is already archived');

        const orgData = orgSnap.data();
        const currentLicense = orgData?.license || {};
        const currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
        let currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;
        const hadLicense = allocationSnap.exists() && allocationSnap.data().status !== 'revoked';

        console.log('[Archive] Reads complete. hadLicense:', hadLicense, 'currentUsed:', currentUsed, 'currentTotal:', currentTotal);

        // --- ALL WRITES (after reads) ---
        transaction.update(memberRef, {
          status: 'archived',
          archivedAt: serverTimestamp(),
          archivedBy: currentUser.uid,
        });

        if (orgUserSnap.exists()) {
          transaction.update(orgUserRef, { status: 'archived' });
        }

        // Note: We do NOT update userRef (activeOrgs) here - Firestore rules only allow
        // the user to write their own doc. Org admins cannot update other users' activeOrgs.
        // The access check below redirects archived members when they try to open the org.

        if (userOrgSnap.exists()) {
          transaction.update(userOrgRef, { status: 'archived' });
        }

        if (hadLicense) {
          console.log('[Archive] Revoking license and releasing to pool. Before: used=', currentUsed);
          transaction.update(allocationRef, {
            status: 'revoked',
            revokedAt: serverTimestamp(),
            revokedBy: currentUser.uid,
          });
          transaction.update(memberRef, {
            licenseAllocated: false,
            licenseStatus: 'revoked',
            licenseAllocatedAt: deleteField(),
            licenseExpiresAt: deleteField(),
          });
          currentUsed = Math.max(0, currentUsed - 1);
          const newAvailable = currentTotal - currentUsed;
          console.log('[Archive] License released. After: used=', currentUsed, 'available=', newAvailable);
          transaction.update(orgRef, {
            'license.disciplemakersUsed': currentUsed,
            'license.disciplemakersAvailable': newAvailable,
            'license.seatsUsed': currentUsed,
            'license.seatsAvailable': newAvailable,
            'license.updatedAt': serverTimestamp(),
          });
          const historyRef = doc(allocationHistoryRef);
          transaction.set(historyRef, {
            userId,
            action: 'revoked',
            revokedBy: currentUser.uid,
            reason: 'member_archived',
            organizationId: orgId,
            timestamp: serverTimestamp(),
          });
        } else {
          console.log('[Archive] No license to release.');
        }
      });

      console.log('[Archive] Transaction complete.');
      toast.success('Member archived. License freed.');
      fetchMembersAndInvites();
    } catch (error) {
      console.error('[Archive] Error archiving member:', error);
      toast.error(error.message || 'Failed to archive member');
    }
  };

  const handleRestoreMember = async (userId) => {
    try {
      const memberRef = doc(db, 'organizations', orgId, 'members', userId);
      const orgUserRef = doc(db, 'organizations', orgId, 'users', userId);
      const userRef = doc(db, 'users', userId);
      const userOrgRef = doc(db, 'users', userId, 'organizations', orgId);
      const allocationRef = doc(db, 'organizations', orgId, 'licenseAllocations', userId);
      const orgRef = doc(db, 'organizations', orgId);
      const allocationHistoryRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');

      let provisionalCount = 0;
      try {
        const provisionalSnap = await getDocs(
          query(
            collection(db, 'organizations', orgId, 'licenseAllocations'),
            where('status', '==', 'provisional')
          )
        );
        provisionalCount = provisionalSnap.size;
      } catch (e) {
        console.warn('Could not count provisional licenses:', e);
      }

      await runTransaction(db, async (transaction) => {
        // --- ALL READS FIRST (Firestore requirement) ---
        const orgSnap = await transaction.get(orgRef);
        const memberSnap = await transaction.get(memberRef);
        const allocationSnap = await transaction.get(allocationRef);
        const orgUserSnap = await transaction.get(orgUserRef);
        const userOrgSnap = await transaction.get(userOrgRef);

        if (!orgSnap.exists() || !memberSnap.exists()) throw new Error('Member or organization not found');

        const memberData = memberSnap.data();
        if (memberData.status !== 'archived') throw new Error('Member is not archived');

        const orgData = orgSnap.data();
        const currentLicense = orgData?.license || {};
        let currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
        let currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;

        const hasActiveLicense = currentLicense.status === 'active' ||
          currentLicense.status === 'trialing' ||
          currentLicense.platformSubscriptionId ||
          currentLicense.stripeSubscriptionId ||
          currentLicense.earlyAdopter === true;
        if (currentTotal === 0 && hasActiveLicense) currentTotal = 5;

        const seatsAvailable = currentTotal - currentUsed;
        let licenseStatus = 'allocated';
        let expiresAt = null;
        if (seatsAvailable <= 0) {
          const maxProvisional = currentLicense.maxProvisional ?? 3;
          if (provisionalCount >= maxProvisional) throw new Error('No licenses available');
          licenseStatus = 'provisional';
          expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
        }

        // --- ALL WRITES (after reads) ---
        transaction.update(memberRef, {
          status: 'active',
          restoredAt: serverTimestamp(),
          restoredBy: currentUser.uid,
        });

        if (orgUserSnap.exists()) {
          transaction.update(orgUserRef, { status: 'active' });
        }

        transaction.update(userRef, { activeOrgs: arrayUnion(orgId) });

        if (userOrgSnap.exists()) {
          transaction.update(userOrgRef, { status: 'active' });
        }

        transaction.set(allocationRef, {
          userId,
          status: licenseStatus,
          allocatedAt: serverTimestamp(),
          allocatedBy: currentUser.uid,
          reason: 'restore_archived_member',
          organizationId: orgId,
          expiresAt,
          revokedAt: null,
          revokedBy: null,
        });

        transaction.update(memberRef, {
          licenseAllocated: true,
          licenseStatus,
          licenseAllocatedAt: serverTimestamp(),
          licenseExpiresAt: expiresAt,
        });

        if (licenseStatus === 'allocated') {
          currentUsed += 1;
          const newAvailable = currentTotal - currentUsed;
          transaction.update(orgRef, {
            'license.disciplemakersUsed': currentUsed,
            'license.disciplemakersAvailable': newAvailable,
            'license.seatsUsed': currentUsed,
            'license.seatsAvailable': newAvailable,
            'license.updatedAt': serverTimestamp(),
          });
        }

        const historyRef = doc(allocationHistoryRef);
        transaction.set(historyRef, {
          userId,
          action: 'allocated',
          status: licenseStatus,
          allocatedBy: currentUser.uid,
          reason: 'restore_archived_member',
          organizationId: orgId,
          timestamp: serverTimestamp(),
        });
      });

      toast.success('Member restored with license allocated.');
      fetchMembersAndInvites();
    } catch (error) {
      console.error('Error restoring member:', error);
      toast.error(error.message || 'Failed to restore member');
    }
  };

  const fetchOrgMetrics = async () => {
    try {
      const now = new Date();
      const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
      const oneQuarterAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);

      // Source of truth: members subcollection (active only)
      const membersRef = collection(db, 'organizations', orgId, 'members');
      const membersSnap = await getDocs(membersRef);
      const activeMembers = membersSnap.docs.filter(d => d.data().status === 'active');

      try {
        let newMembersLastWeek = 0;
        let newMembersLastMonth = 0;
        let newMembersLastQuarter = 0;
        let activeUsersThisWeek = 0;
        let activeUsersThisMonth = 0;

        activeMembers.forEach(d => {
          const userData = d.data();
          const joinDate = userData.joinedAt?.toDate();
          const lastActive = userData.lastActivityAt?.toDate();

          if (joinDate) {
            if (joinDate >= oneWeekAgo) newMembersLastWeek++;
            if (joinDate >= oneMonthAgo) newMembersLastMonth++;
            if (joinDate >= oneQuarterAgo) newMembersLastQuarter++;
          }

          if (lastActive) {
            if (lastActive >= oneWeekAgo) activeUsersThisWeek++;
            if (lastActive >= oneMonthAgo) activeUsersThisMonth++;
          }
        });

        // Get groups count
        const groupsQuery = query(collection(db, 'groups'), 
          where('orgID', '==', `/organizations/${orgId}`));
        const groupsSnap = await getDocs(groupsQuery);

        setOrgMetrics({
          activeMembers: activeMembers.length,
          totalMembers: activeMembers.length,
          pendingRequests: 0,
          totalGroups: groupsSnap.size,
          memberTrends: {
            lastWeek: newMembersLastWeek,
            lastMonth: newMembersLastMonth,
            lastQuarter: newMembersLastQuarter
          },
          activityStats: {
            lastActive: organization.lastActivityAt,
            activeUsersThisWeek,
            activeUsersThisMonth
          }
        });
      } catch (error) {
        if (error.code === 'failed-precondition') {
          console.error('Error fetching org metrics:', error.message);
          setOrgMetrics({
            activeMembers: 0,
            totalMembers: 0,
            pendingRequests: 0,
            totalGroups: 0,
            memberTrends: { lastWeek: 0, lastMonth: 0, lastQuarter: 0 },
            activityStats: { lastActive: null, activeUsersThisWeek: 0, activeUsersThisMonth: 0 },
          });
        }
      }
    } catch (outerError) {
      console.error('Error in fetchOrgMetrics:', outerError);
    }
  };

  const toggleStoryVisibility = async (story) => {
    try {
      // Find the story reference document in the organization's subcollection
      const storyRefQuery = query(
        collection(db, 'organizations', orgId, 'stories'),
        where('storyId', '==', story.id),
        where('authorId', '==', story.authorId)
      );
      
      const storyRefSnapshot = await getDocs(storyRefQuery);

      if (storyRefSnapshot.empty) {
        toast.error("Could not find the story reference to update.");
        console.error("No story reference found for storyId:", story.id, "and authorId:", story.authorId);
        return;
      }

      // There should only be one, so we'll update the first one found.
      const storyRefDoc = storyRefSnapshot.docs[0];
      const newVisibility = !story.visible;

      await updateDoc(storyRefDoc.ref, {
        visible: newVisibility,
        lastUpdated: serverTimestamp()
      });

      // Optimistically remove the story from the local state for a smoother UX
      setOrgStories(prev => prev.filter(s => s.id !== story.id));
      toast.success(`Story is now ${newVisibility ? 'visible' : 'hidden'}.`);

    } catch (error) {
      console.error('Error toggling story visibility:', error);
      toast.error('Failed to update story visibility.');
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchMembersAndInvites();
    }
  }, [orgId]);

  // Add handler for vitals updates
  const handleVitalsSave = async () => {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      await updateDoc(orgRef, {
        vitals: editedVitals
      });
      
      setOrganization(prev => ({
        ...prev,
        vitals: editedVitals
      }));
      
      setIsEditingVitals(false);
    } catch (error) {
      console.error('Failed to update vitals:', error);
    }
  };

  // Add this effect to fetch stories
  useEffect(() => {
    if (!orgId) return;

    const fetchOrgStories = async () => {
      setLoading(true);
      try {
        const storiesRef = collection(db, 'organizations', orgId, 'stories');
        // Fetch all stories (we'll filter and sort in memory for backward compatibility)
        // Note: Can't use orderBy('sharedAt') if some stories don't have it
        // So we fetch all and sort in memory
        const storiesSnapshot = await getDocs(storiesRef);
        
        const storyPromises = storiesSnapshot.docs.map(async (storyRefDoc) => {
          const storyRefData = storyRefDoc.data();
          
          // Skip collection init documents
          if (storyRefDoc.id === '_collection_init') return null;
          
          // Filter for visible stories: visible == true OR visible field doesn't exist (backward compatibility)
          if (storyFilter === 'visible') {
            const isVisible = storyRefData.visible !== false; // true if visible is true or undefined
            if (!isVisible) return null; // Skip hidden stories
          }
          
          // Try to get full story from user's stories collection
          // Use storyRefData.id (if it exists) or storyRefDoc.id to find the story
          const storyIdToFetch = storyRefData.id || storyRefDoc.id;
          let storyData = storyRefData; // Use org story data as fallback
          
          if (storyRefData.authorId && storyIdToFetch) {
            try {
              const storyDoc = await getDoc(doc(db, 'users', storyRefData.authorId, 'stories', storyIdToFetch));
              if (storyDoc.exists()) {
                storyData = { ...storyDoc.data(), id: storyDoc.id };
              }
            } catch (error) {
              console.warn('Could not fetch full story data, using org story data:', error);
            }
          }
          
          return {
            ...storyData,
            id: storyData.id || storyRefDoc.id, // The actual story ID
            authorId: storyRefData.authorId || storyData.authorId,
            visible: storyRefData.visible !== false, // Default to true if not set (backward compatibility)
            sharedAt: storyRefData.sharedAt || storyData.createdAt, // Use sharedAt or createdAt for sorting
          };
        });

        const stories = (await Promise.all(storyPromises)).filter(Boolean);
        
        // Sort by sharedAt (or createdAt) descending
        stories.sort((a, b) => {
          const aTime = a.sharedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.sharedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime; // Descending
        });
        
        setOrgStories(stories);
      } catch (error) {
        console.error('Error fetching organization stories:', error);
        // If you still get an index error, Firestore will provide a link to create it.
        // It's a one-time setup for the whole app.
        if (error.code === 'failed-precondition') {
          toast.error("A database index is required. Check the console for a link to create it.");
          console.error("Firestore index creation required. See the error message for the link to create the index in the Firebase console.", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrgStories();
  }, [orgId, storyFilter]);

  // Fetch organization content tracks
  useEffect(() => {
    if (!orgId) return;

    const fetchOrgContentTracks = async () => {
      setLoadingTracks(true);
      try {
        const tracksRef = collection(db, 'contentTracks');
        const tracksQuery = query(
          tracksRef,
          where('organizationId', '==', orgId)
        );
        const tracksSnapshot = await getDocs(tracksQuery);
        
        const tracksData = tracksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by creation date (newest first)
        tracksData.sort((a, b) => {
          const aDate = a.dateCreated?.toDate?.() || new Date(0);
          const bDate = b.dateCreated?.toDate?.() || new Date(0);
          return bDate - aDate;
        });

        setOrgContentTracks(tracksData);
      } catch (error) {
        console.error('Error fetching organization content tracks:', error);
      } finally {
        setLoadingTracks(false);
      }
    };

    fetchOrgContentTracks();
  }, [orgId]);

  console.log('Render state:', { isAdmin, orgId, currentUser: currentUser?.uid, userRole });

  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header Section */}
        <div className="p-4 sm:p-6 relative">
          {/* Settings Cog - Absolute positioned in top right */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
          </button>

          {/* Profile Header */}
          <div className="flex items-start gap-4">
            {/* Logo Container */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full overflow-hidden flex items-center justify-center">
                <ImageUpload
                  currentImage={logoUrl}
                  onUploadSuccess={handleLogoUpload}
                  userId={orgId}
                  path={`organizations/${orgId}/logos`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Content Container */}
            <div className="flex-1 min-w-0 pr-8">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-800 truncate">
                {organization.name || organization.displayName}
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 line-clamp-3 sm:line-clamp-none">
                {organization.bio}
              </p>
              {organization.vitals?.website && (
                <a 
                  href={organization.vitals.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 sm:mt-2 text-sm text-[#3274B8] hover:underline block truncate"
                >
                  {formatWebsiteUrl(organization.vitals.website)}
                </a>
              )}
            </div>
          </div>

          {/* Admin tabs */}
          {isAdmin && (
            <>
              <div className="mt-8 border-b border-gray-200">
                <nav className="flex flex-wrap gap-1 sm:gap-2 -mb-px" aria-label="Tabs">
                  {[
                    { id: 'community', label: 'Community' },
                    { id: 'announcements', label: 'Announcements' },
                    { id: 'members', label: 'Members', badge: pendingRequests.length + pendingInvites.length },
                    { id: 'content-tracks', label: 'Content Tracks' },
                    { id: 'account', label: 'Manage Account' },
                    { id: 'stats', label: 'Great Commission Stats' },
                    { id: 'welcome', label: 'Welcome Settings' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        if (isInvitePath) {
                          navigate(`/organization/${orgId}?tab=${tab.id}`);
                        } else {
                          setAdminTab(tab.id);
                        }
                      }}
                      className={classNames(
                        'py-3 px-3 sm:px-4 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap',
                        adminTab === tab.id
                          ? 'border-[#3274B8] text-[#3274B8] bg-blue-50/50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      )}
                    >
                      {tab.label}
                      {tab.badge > 0 && (
                        <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab content */}
              <div className="mt-6 min-h-[320px]">
                {isInvitePath ? (
                  <div className="p-4">
                    <InviteMembers
                      embedded
                      onInvitationSent={async () => {
                        await fetchMembersAndInvites();
                        navigate(`/organization/${orgId}?tab=members&subtab=invitations`);
                      }}
                    />
                  </div>
                ) : adminTab === 'community' && (
                  <>
          <div className="p-4">
            {/* Community sub-tabs: Stories | Activity Feed */}
            <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
              <button
                onClick={() => setCommunitySubTab('stories')}
                className={classNames(
                  'px-4 py-2 text-sm font-medium rounded-lg',
                  communitySubTab === 'stories'
                    ? 'bg-[#3274B8] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Stories
              </button>
              <button
                onClick={() => setCommunitySubTab('activity')}
                className={classNames(
                  'px-4 py-2 text-sm font-medium rounded-lg',
                  communitySubTab === 'activity'
                    ? 'bg-[#3274B8] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Activity Feed
              </button>
            </div>
            {communitySubTab === 'stories' && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Manage Stories</h2>
              
              <div className="flex gap-2 mb-4">
                <button
                  className={`px-4 py-2 rounded-full ${
                    storyFilter === 'visible' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                  } transition-colors`}
                  onClick={() => setStoryFilter('visible')}
                >
                  Visible
                </button>
                <button
                  className={`px-4 py-2 rounded-full ${
                    storyFilter === 'hidden' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-600'
                  } transition-colors`}
                  onClick={() => setStoryFilter('hidden')}
                >
                  Hidden
                </button>
              </div>
              
              {/* Stories List */}
              <div className="space-y-4 mt-6">
                {orgStories.length > 0 ? (
                  orgStories.map(story => (
                    <div key={story.id} className="bg-white shadow rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={story.authorPhotoURL || '/default-avatar.png'} 
                            alt="" 
                            className="h-10 w-10 rounded-full"
                          />
                          <div>
                            <h3 className="font-medium">{story.authorName}</h3>
                            <p className="text-sm text-gray-500">
                              {story.createdAt?.toDate().toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleStoryVisibility(story)}
                          className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                            story.visible
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {story.visible ? 'Hide Story' : 'Show Story'}
                        </button>
                      </div>
                      <p className="text-gray-700">{story.text}</p>
                      {/* Video Content */}
                      {story.playableURL && (
                        <div className="mt-3 flex justify-center">
                          <div className="w-full max-w-md sm:max-w-lg md:max-w-xl">
                            <div className="bg-gray-100 rounded-lg overflow-hidden relative mx-auto" style={{ aspectRatio: '9/16', maxWidth: '500px' }}>
                              <video
                                className="w-full h-full object-contain rounded-lg"
                                controls
                                preload="metadata"
                                src={story.playableURL}
                                onError={(e) => console.error('Video error:', e)}
                                onLoadedData={(e) => {
                                  console.log('Video loaded successfully:', story.playableURL);
                                  // Hide loading overlay when video loads
                                  const loadingOverlay = e.target.parentElement.querySelector('.loading-overlay');
                                  if (loadingOverlay) {
                                    loadingOverlay.style.display = 'none';
                                  }
                                }}
                              >
                                Your browser does not support the video tag.
                              </video>
                              {/* Loading overlay */}
                              <div className="loading-overlay absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 rounded-lg">
                                <div className="text-gray-600 text-sm">Loading video...</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {story.metrics && (
                        <div className="mt-4 flex gap-4 text-sm text-gray-600">
                          <span>👥 {story.metrics.spiritualDiscussions || 0} Discussions</span>
                          <span>❤️ {story.metrics.experiencedLove || 0} Experienced Love</span>
                          <span>✨ {story.metrics.newFollowers || 0} New Followers</span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">
                    No {storyFilter} stories have been shared with this organization yet.
                  </p>
                )}
              </div>
          </div>
            )}
            {communitySubTab === 'activity' && (
              <OrganizationActivityFeed orgId={orgId} />
            )}
          </div>
                </>
                )}

                {adminTab === 'announcements' && (
                  <div className="p-4">
                    <h2 className="text-2xl font-bold mb-4">Announcements</h2>
                    <p className="text-gray-600 mb-4">Send a message to all members of this organization. Past announcements appear below; you can edit, delete, or archive them.</p>
                    <button
                      onClick={() => setIsMessageDrawerOpen(true)}
                      className="px-4 py-2 bg-[#3274B8] text-white rounded-lg hover:bg-[#2861A1] flex items-center gap-2 mb-6"
                    >
                      <PlusIcon className="h-5 w-5" />
                      Make an Announcement
                    </button>
                    <OrganizationAnnouncementsList
                      key={announcementsRefreshKey}
                      organizationId={orgId}
                      isAdmin={isAdmin}
                    />
                  </div>
                )}

                {adminTab === 'stats' && (
                  <OrganizationGreatCommissionStats orgId={orgId} />
                )}

                {adminTab === 'members' && (
                  <div className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div>
                        <h2 className="text-2xl font-bold">Members</h2>
                        <p className="text-gray-600 mt-1">
                          {memberCount} active members · {pendingRequests.length} pending requests · {pendingInvites.length} invitations
                        </p>
                      </div>
                      <button
                        onClick={() => navigate(`/organization/${orgId}/invite`)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#3274B8] text-white rounded-lg hover:bg-[#2861A1] text-sm font-medium"
                      >
                        <PlusIcon className="h-5 w-5" />
                        Invite Members
                      </button>
                    </div>
                    <MemberSearchWidget
                      members={memberData}
                      archivedMembers={archivedMemberData}
                      pendingRequests={pendingRequests}
                      invitations={pendingInvites}
                      organizationId={orgId}
                      onRoleChange={handleRoleChange}
                      onRequestAction={handleRequestAction}
                      onResend={handleResend}
                      onArchiveMember={handleArchiveMember}
                      onRestoreMember={handleRestoreMember}
                      onMemberCountChange={(count) => setMemberCount(count)}
                      loading={loading}
                      defaultTab={searchParams.get('subtab') === 'invitations' ? 'invitations' : (pendingRequests.length > 0 ? 'pending' : 'members')}
                    />
                  </div>
                )}

                {adminTab === 'account' && (
                  <div className="p-4">
                    <OrganizationSubscription embedded />
                  </div>
                )}

                {adminTab === 'welcome' && (
                  <OrganizationWelcomeSettings orgId={orgId} />
                )}

                {adminTab === 'content-tracks' && (
                  <>
            <div className="mt-8">
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">Content Tracks</h2>
                  <Link
                    to={`/organizations/${orgId}/content-tracks/create`}
                    className="inline-flex items-center px-4 py-2 bg-[#5ABA47] text-white rounded-lg hover:bg-[#4a9939] transition-colors text-sm font-medium"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create New Track
                  </Link>
                </div>
                
                {loadingTracks ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-2 text-gray-500 text-sm">Loading tracks...</p>
                  </div>
                ) : orgContentTracks.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 mb-4">No content tracks yet</p>
                    <Link
                      to={`/organizations/${orgId}/content-tracks/create`}
                      className="inline-flex items-center px-4 py-2 bg-[#5ABA47] text-white rounded-lg hover:bg-[#4a9939] transition-colors text-sm font-medium"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Create Your First Track
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                    {orgContentTracks.map(track => (
                      <div key={track.id} className="bg-white shadow rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{track.name}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                              {track.description || 'No description'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {track.approvalStatus === 'pending' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending Approval
                            </span>
                          )}
                          {track.approvalStatus === 'approved' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Approved
                            </span>
                          )}
                          {track.approvalStatus === 'rejected' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Rejected
                            </span>
                          )}
                          {track.visibility?.isPublic ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Public
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Org Only
                            </span>
                          )}
                          {track.status && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              track.status === 'published' 
                                ? 'bg-green-100 text-green-800'
                                : track.status === 'draft'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {track.status}
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2 pt-3 border-t border-gray-200">
                          <Link
                            to={`/content-tracks/${track.id}`}
                            className="flex-1 text-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                          >
                            View
                          </Link>
                          <Link
                            to={`/content-tracks/${track.id}/edit`}
                            className="flex-1 text-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                          >
                            Edit
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Non-admin: show summary and link to subscription */}
          {!isAdmin && (
            <div className="mt-8 p-4">
              <p className="text-gray-600 mb-4">{memberCount} members · {discipleCount} disciples</p>
              <p className="text-sm text-gray-500">Created: {organization.createdAt?.toDate()?.toLocaleDateString() || 'N/A'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Settings Drawer */}
      <Drawer 
        open={showSettings} 
        onClose={() => setShowSettings(false)}
        title="Organization Settings"
        subtitle={organization?.name || ''}
        avatar={organization?.logoUrl}
      >
        <div className="space-y-6 py-4">
              {/* Organization Bio */}
              <div className="space-y-4 p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium">Organization Bio</h3>
                <textarea
                  value={editedSettings.bio || organization.bio}
                  onChange={(e) => setEditedSettings(prev => ({
                    ...prev,
                    bio: e.target.value
                  }))}
                  placeholder="Enter organization bio..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                />
              </div>

              {/* Organization Vitals */}
              <div className="space-y-4 p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium">Organization Details</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <select
                      value={editedVitals.country || organization.vitals?.country || 'US'}
                      onChange={(e) => setEditedVitals(prev => ({ ...prev, country: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      {/* Add more countries as needed */}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Zip Code</label>
                    <input
                      type="text"
                      value={editedVitals.zipCode || organization.vitals?.zipCode || ''}
                      onChange={(e) => setEditedVitals(prev => ({ ...prev, zipCode: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter zip code"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Website</label>
                  <input
                    type="url"
                    value={editedVitals.website || organization.vitals?.website || ''}
                    onChange={(e) => setEditedVitals(prev => ({ ...prev, website: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              {/* Organization Access Settings */}
              <div className="space-y-4 p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium">Organization Access Settings</h3>
                
                {/* Visibility Setting */}
                <div className="space-y-2">
                  <label className="font-medium">Organization Visibility</label>
                  <div className="flex items-center space-x-4">
                    <select
                      value={editedSettings.visibility}
                      onChange={(e) => setEditedSettings(prev => ({
                        ...prev,
                        visibility: e.target.value
                      }))}
                      className="rounded-md border p-2"
                    >
                      <option value="public">Public - Anyone can find this organization</option>
                      <option value="private">Private - Invite only</option>
                    </select>
                  </div>
                </div>

                {/* Require Approval to join? - Only applies when org is public (discoverable) */}
                {editedSettings.visibility === 'public' && (
                  <div className="space-y-2">
                    <label className="font-medium flex items-center justify-between">
                      <span>Require Approval to join?</span>
                      <button
                        type="button"
                        onClick={() => setEditedSettings(prev => ({
                          ...prev,
                          requireAdminApproval: !prev.requireAdminApproval
                        }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${editedSettings.requireAdminApproval ? 'bg-blue-600' : 'bg-gray-200'}`}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${editedSettings.requireAdminApproval ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </label>
                  </div>
                )}

                {/* Allow Members to Invite */}
                <div className="space-y-2">
                  <label className="font-medium flex items-center justify-between">
                    <span>Allow Members to Invite</span>
                    <button
                      type="button"
                      onClick={() => setEditedSettings(prev => ({
                        ...prev,
                        allowMembersToInvite: !prev.allowMembersToInvite
                      }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${editedSettings.allowMembersToInvite ? 'bg-blue-600' : 'bg-gray-200'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${editedSettings.allowMembersToInvite ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </label>
                </div>

                <div className="text-sm text-gray-500 space-y-1">
                  {editedSettings.visibility === 'private' ? (
                    <p>Members can only join through direct invitations. Invitation links bypass approval.</p>
                  ) : editedSettings.requireAdminApproval ? (
                    <p>Users can find and request to join. Admins must approve requests before they receive a license. (Recommended)</p>
                  ) : (
                    <p>Users can find and join automatically without approval. Not recommended — they will use a license seat immediately.</p>
                  )}
                  <p>
                    {editedSettings.allowMembersToInvite
                      ? "Any member can invite new people. Invited users still follow the approval setting above if they join via request."
                      : "Only admins can send invitations."}
                  </p>
                </div>
              </div>

              {/* Default Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Role
                </label>
                <select
                  value={editedSettings.defaultRole}
                  onChange={(e) => setEditedSettings(prev => ({
                    ...prev,
                    defaultRole: e.target.value
                  }))}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Member">Member</option>
                  <option value="Moderator">Moderator</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <input
                  type="color"
                  value={editedSettings.primaryColor}
                  onChange={(e) => setEditedSettings(prev => ({
                    ...prev,
                    primaryColor: e.target.value
                  }))}
                  className="w-20 h-10 p-1 rounded border"
                />
              </div>

              {/* Story Settings */}
              <div className="space-y-2">
                <label className="block font-medium text-gray-700">Story Settings</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    id="autoApproveStories"
                    checked={editedSettings.storySettings?.autoApprove}
                    onChange={(e) => setEditedSettings(prev => ({
                      ...prev,
                      storySettings: {
                        ...prev.storySettings,
                        autoApprove: e.target.checked
                      }
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="autoApproveStories" className="text-sm text-gray-700">
                    Auto-approve member stories
                  </label>
                </div>
              </div>

              {/* Counseling Resource URL */}
              <div className="space-y-4 p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <span role="img" aria-label="heart">💜</span> Counseling Resource
                </h3>
                <p className="text-sm text-gray-500">
                  Provide a link to your church's preferred counseling or mental health resource. Members will see this when they need help with a disciple.
                </p>
                <input
                  type="url"
                  value={counselingUrl}
                  onChange={(e) => setCounselingUrl(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/counseling"
                />
              </div>
        </div>

        {/* Save Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 mt-4">
          <button
            onClick={() => {
              handleSettingsSave();
              handleVitalsSave();
              setShowSettings(false);
            }}
            className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            Save All Changes
          </button>
        </div>
      </Drawer>


      <Drawer open={isMessageDrawerOpen} onClose={() => setIsMessageDrawerOpen(false)}>
        <OrgMessageWriter
          organizationId={orgId}
          onSuccess={() => {
            setIsMessageDrawerOpen(false);
            setAnnouncementsRefreshKey((k) => k + 1);
          }}
        />
      </Drawer>
    </div>
  );
}

export default Organization;
